import { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface AddressAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onSelect: (addressData: {
        address: string;
        city: string;
        zip_code: string;
        country: string;
        country_code?: string;
        region: string;
        lat?: number;
        lon?: number;
    }) => void;
    placeholder?: string;
    className?: string;
    defaultCountry?: string;
    countryCode?: string; // ISO 2-letter code
    type?: 'address' | 'city';
    isRelative?: boolean;
    premium?: boolean;
    city?: string;
}

export default function AddressAutocomplete({
    value,
    onChange,
    onSelect,
    placeholder,
    className,
    defaultCountry,
    countryCode,
    type = 'address',
    isRelative,
    premium = false,
    city
}: AddressAutocompleteProps) {
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Click outside to close
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const handleInput = (val: string) => {
        onChange(val);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (val.length < (type === 'city' ? 2 : 3)) {
            setSuggestions([]);
            setShowSuggestions(false);
            setLoading(false);
            return;
        }

        setLoading(true);

        timeoutRef.current = setTimeout(async () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();

            try {
                // Build query
                let query = val;
                if (type === 'address' && city) {
                    query = `${val}, ${city}`;
                }

                let url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=5`;

                if (countryCode) {
                    url += `&countrycodes=${countryCode.toLowerCase()}`;
                }

                if (type === 'city') {
                    url += `&featuretype=settlement`;
                }

                const res = await fetch(url, {
                    headers: { 'User-Agent': 'PaddleScore/1.0' },
                    signal: abortControllerRef.current?.signal
                });

                if (!res.ok) throw new Error('Search failed');
                let data = await res.json();

                // Advanced filtering for "city" type
                if (type === 'city') {
                    data = data.filter((item: any) => {
                        const addr = item.address || {};
                        return addr.city || addr.town || addr.village || addr.municipality;
                    });
                }

                setSuggestions(data);
                setShowSuggestions(true);
            } catch (e: any) {
                if (e.name !== 'AbortError') {
                    console.warn(e);
                    if (!suggestions.length) setShowSuggestions(false);
                }
            } finally {
                if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
                    setLoading(false);
                }
            }
        }, 500);
    };

    const handleSelect = (item: any) => {
        // Extract structured data
        const addr = item.address || {};
        let city = addr.city || addr.town || addr.village || addr.municipality || addr.suburb || addr.hamlet || addr.city_district || addr.neighbourhood || '';
        let zip = addr.postcode || '';
        const country = addr.country || defaultCountry || 'France';
        const region = addr.region || addr.state || addr.province || addr.county || '';

        // --- HARVESTING FALLBACKS ---
        // If zip is missing, try to find a 4-7 digit number in the display name
        if (!zip) {
            const zipMatch = item.display_name.match(/\b\d{4,7}\b/);
            if (zipMatch) zip = zipMatch[0];
        }

        // If city is missing, try to infer it from display_name parts
        if (!city) {
            const parts = item.display_name.split(',').map((p: string) => p.trim());
            // Usually: [Street/Place, ..., City/Town, Region, Zip/Country]
            // We search from the end, skipping country and the part containing the zip
            for (let i = parts.length - 2; i >= 0; i--) {
                const part = parts[i];
                if (zip && part.includes(zip)) continue;
                if (region && (part === region || region.includes(part))) continue;
                city = part;
                break;
            }
        }

        // 1. Better street address extraction: join all parts before city/zip
        const displayParts = item.display_name.split(',').map((p: string) => p.trim());
        const addressParts = [];
        const skipList = [city, zip, country, addr.state, addr.region, addr.county, addr.province].filter(Boolean);

        for (const part of displayParts) {
            if (skipList.some(skip => part.includes(skip) || skip.includes(part))) break;
            addressParts.push(part);
        }

        let shortAddress = addressParts.length > 0 ? addressParts.join(', ') : displayParts[0];

        // 2. Recover house number if missing from API but present in user input
        // Only if shortAddress doesn't already seem to have a number
        if (!/\d/.test(shortAddress) && value) {
            const numAtStart = value.match(/^(\d+[a-zA-Z]?)\s+/);
            const numAtEnd = value.match(/\s+(\d+[a-zA-Z]?)$/);
            if (numAtStart) shortAddress = `${numAtStart[1]} ${shortAddress}`;
            else if (numAtEnd) shortAddress = `${shortAddress} ${numAtEnd[1]}`;
        }

        onSelect({
            address: shortAddress,
            city: city,
            zip_code: zip,
            country: country,
            country_code: addr.country_code?.toUpperCase(),
            region: region,
            lat: parseFloat(item.lat),
            lon: parseFloat(item.lon)
        });

        onChange(shortAddress);
        setShowSuggestions(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && showSuggestions && suggestions.length > 0) {
            e.preventDefault();
            handleSelect(suggestions[0]);
        }
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <div className="relative group">
                {type === 'city' ? (
                    <MapPin className={`absolute left-4 top-3.5 transition-colors duration-300 ${premium ? 'text-blue-400' : 'text-slate-500'}`} size={18} />
                ) : (
                    <MapPin className={`absolute left-4 top-3.5 transition-colors duration-300 ${premium ? 'text-indigo-400' : 'text-slate-500'}`} size={18} />
                )}

                <input
                    type="text"
                    value={value}
                    onChange={(e) => handleInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder || (type === 'city' ? "Ex: Paris" : "Ex: 12 Rue de la Paix")}
                    className={`
                        w-full pl-12 pr-10 py-3 transition-all duration-300 outline-none
                        ${premium
                            ? 'bg-slate-900/50 border-slate-700/50 text-white rounded-xl focus:ring-2 focus:ring-blue-500 backdrop-blur-sm'
                            : 'bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500'
                        }
                        ${className || ''}
                    `}
                    autoComplete="off"
                />

                {loading && (
                    <div className="absolute right-4 top-3.5">
                        <Loader2 className="animate-spin text-blue-500" size={18} />
                    </div>
                )}
            </div>

            <AnimatePresence>
                {showSuggestions && suggestions.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className={`
                            ${isRelative ? 'relative' : 'absolute'} z-[500] w-full mt-2 max-h-60 overflow-y-auto custom-scrollbar shadow-2xl overflow-hidden
                            ${premium
                                ? 'bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl'
                                : 'bg-white border border-slate-100 rounded-xl'
                            }
                        `}
                    >
                        {suggestions.map((item, i) => (
                            <button
                                key={i}
                                type="button"
                                onMouseDown={(e) => {
                                    e.preventDefault(); // Prevent blur
                                    handleSelect(item);
                                }}
                                className={`
                                    w-full text-left px-4 py-3 transition-colors flex items-start gap-3 group
                                    ${premium
                                        ? 'hover:bg-white/5 border-b border-white/5 last:border-0'
                                        : 'hover:bg-blue-50 border-b border-slate-50 last:border-0'
                                    }
                                `}
                            >
                                <div className={`
                                    mt-1 shrink-0 transition-transform duration-300 group-hover:scale-110
                                    ${premium ? 'text-blue-400' : 'text-slate-400'}
                                `}>
                                    <MapPin size={16} />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <div className={`font-semibold text-sm truncate ${premium ? 'text-white' : 'text-slate-800'}`}>
                                        {item.display_name.split(',')[0]}
                                    </div>
                                    <div className={`text-[10px] truncate ${premium ? 'text-slate-400' : 'text-slate-500'}`}>
                                        {item.display_name}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
